import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeSlideUp, useSnapVariants } from '../../lib/motion';

type ToastVariant = 'default' | 'destructive' | 'success';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  onClose?: () => void;
  action?: ToastAction;
}

// 토스트 메시지 전역 상태
const toastQueue: ToastProps[] = [];
let addToast: (toast: ToastProps) => void = () => {}; // 기본 빈 함수
let removeToast: (index: number) => void = () => {}; // 기본 빈 함수

/**
 * 토스트 알림 생성 함수
 * @param props 토스트 속성
 */
export const toast = (props: ToastProps) => {
  // 등록된 addToast 사용
  addToast({
    ...props,
    duration: props.duration || 3000, // 기본 3초
    variant: props.variant || 'default'
  });
};

/**
 * 토스트 컨테이너 컴포넌트
 * - 토스트 메시지를 관리하고 표시
 */
export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<(ToastProps & { id: number })[]>([]);
  const [counter, setCounter] = useState(0);
  const snapFadeSlideUp = useSnapVariants(fadeSlideUp);
  
  // 토스트 추가 함수
  const handleAddToast = (toast: ToastProps) => {
    const id = counter;
    setCounter(prevCounter => prevCounter + 1);
    setToasts(prevToasts => [...prevToasts, { ...toast, id }]);
    
    // 자동 제거 타이머 설정
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        handleRemoveToast(id);
      }, toast.duration);
    }
  };
  
  // 토스트 제거 함수
  const handleRemoveToast = (id: number) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };
  
  // 전역 함수 등록
  useEffect(() => {
    addToast = handleAddToast;
    removeToast = handleRemoveToast;
    
    return () => {
      addToast = () => {};
      removeToast = () => {};
    };
  }, []);
  
  // 포탈 생성 및 렌더링
  return createPortal(
    <div className="fixed bottom-0 right-0 z-50 p-4 space-y-4 max-w-md w-full pointer-events-none">
      <AnimatePresence initial={false}>
        {toasts.map(toast => (
          <motion.div
            key={toast.id}
            variants={snapFadeSlideUp}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`
              rounded-lg shadow-lg p-4
              pointer-events-auto
              ${toast.variant === 'destructive' ? 'bg-red-600 text-white' :
                toast.variant === 'success' ? 'bg-green-600 text-white' :
                'bg-gray-800 text-white'}
            `}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{toast.title}</h3>
                {toast.description && (
                  <p className="text-sm mt-1">{toast.description}</p>
                )}
                {toast.action && (
                  <button
                    className="mt-2 text-sm font-medium underline opacity-90 hover:opacity-100 transition-opacity"
                    onClick={() => {
                      toast.action!.onClick();
                      handleRemoveToast(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button
                className="text-white hover:opacity-80 transition-opacity"
                onClick={() => handleRemoveToast(toast.id)}
                aria-label="Close notification"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default ToastContainer; 